import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Clock, X } from '@phosphor-icons/react';
import { Span } from 'src/types';
import type { ProposalCardFormData } from '../ProposalConfirmationFieldsGroup';
import {
  useMeetingWindowDateRange,
  DateRangeErrorType,
} from '../../../CreateMeetingCard/hooks/useMeetingWindowDateRange';
import { useMeetingCardFieldsWatch } from '../../../CreateMeetingCard/hooks/useMeetingCardFieldsWatch';
import { DateTimePickerInput } from '../../../CreateMeetingCard/components/MeetingWindowsField/components/DateTimePickerInput';
import { useBreakpoint } from 'src/hooks';
import dayjs from 'dayjs';
import { roundToNearestQuarterHour } from 'src/utils';

const SVG_SIZE = 20;

interface ProposalTimeSlotDateRangePickerProps {
  dateRange: Span;
  index: number;
  onRemove?: () => void;
}

export const ProposalTimeSlotDateRangePicker = ({
  dateRange,
  index,
  onRemove,
}: ProposalTimeSlotDateRangePickerProps) => {
  const {
    formState: { errors },
  } = useFormContext<ProposalCardFormData>();

  const { executed, minutes } =
    useMeetingCardFieldsWatch<ProposalCardFormData>();

  const {
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    updateDateRange,
    validateDateRange,
  } = useMeetingWindowDateRange<ProposalCardFormData>({
    ...dateRange,
    index,
    key: 'proposals',
  });

  const { isMobile } = useBreakpoint();

  useEffect(() => {
    validateDateRange({ start: startDate, end: endDate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minutes]);

  const handleStartChange = (date: string) => {
    setStartDate(date);

    const newMeetingWindow: Span = {
      start: date,
      end: endDate,
    };

    const isValidate = validateDateRange(newMeetingWindow);

    if (!isValidate) {
      const newEndDate = dayjs(date)
        .add(minutes || 0, 'minute')
        .utc();

      const newEndDateString = roundToNearestQuarterHour(newEndDate).format();

      const tmp = {
        start: date,
        end: newEndDateString,
      };

      setEndDate(newEndDateString);
      updateDateRange(tmp);
    } else {
      updateDateRange(newMeetingWindow);
    }
  };

  const handleEndChange = (date: string) => {
    setEndDate(date);

    const newMeetingWindow: Span = {
      start: startDate,
      end: date,
    };

    updateDateRange(newMeetingWindow);
  };

  const hasErrorStartInput =
    Array.isArray(errors.proposals) &&
    (errors.proposals[index]?.type === DateRangeErrorType.BOTH_PICKERS ||
      errors.proposals[index]?.type === DateRangeErrorType.START_PICKER);

  const hasErrorEndInput =
    Array.isArray(errors.proposals) &&
    errors.proposals[index]?.type === DateRangeErrorType.BOTH_PICKERS;

  return (
    <div className="nj-create-meeting-card--meeting-window-picker">
      <Clock size={SVG_SIZE} />

      <div className="nj-create-meeting-card--meeting-window-picker-wrapper">
        <DateTimePickerInput
          date={startDate}
          onDateChange={handleStartChange}
          hasError={hasErrorStartInput}
        />

        {!isMobile && (
          <hr className="nj-create-meeting-card--meeting-window-dash" />
        )}

        <DateTimePickerInput
          date={endDate}
          onDateChange={handleEndChange}
          hasError={hasErrorEndInput}
        />
      </div>

      {!executed && onRemove && (
        <button onClick={onRemove}>
          <X size={16} />
        </button>
      )}

      {!isMobile && errors.proposals && errors.proposals[index]?.message && (
        <span className="nj-create-meeting-card--error-message">
          {errors.proposals[index]?.message}
        </span>
      )}
    </div>
  );
};
